/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import type {ChangeEvent} from 'react';
import {useEffect, useState} from 'react';
import {
	Form,
	InputGroup,
	Button,
	Tooltip,
	OverlayTrigger,
} from 'react-bootstrap';
// eslint-disable-next-line import/named
import {confirmAlert} from 'react-confirm-alert';
import {toast} from 'react-toastify';
import DropdownSumma from 'components/Custom/Dropdown';
import FieldSetSumma from 'components/Custom/FieldGroup';

import type {IConditionForm, DataPlanTemplate} from 'interfaces';

import {useLazyGetAllPlansQuery} from 'services';

const bitRatePattern = /^\d+\s(?:bps|Kbps|Mbps|Gbps|Tbps)$/;

// validations fields
const validationRules = {
	tac: {
		regex: /(^[A-Fa-f0-9]{4}$)/,
		errorMessage: 'The field must contain 4 hexadecimal characters.',
	},
	area: {
		regex: /^.+$/,
		errorMessage: 'The field is invalid.',
	},
	anGwAddress: {
		regex: /^.+$/,
		errorMessage: 'The field is invalid.',
	},
	deviceTac: {
		regex: /^[A-Fa-f0-9]{8}$/g,
		errorMessage: 'The field must contain 8 hexadecimal characters.',
	},
	deviceModel: {
		regex: /^.+$/,
		errorMessage: 'The field is invalid.',
	},
	mcc: {
		regex: /^([a-zA-Z0-9]{3})$/,
		errorMessage: 'The field must contain 3 characters.',
	},
	mnc: {
		regex: /^([a-zA-Z0-9]{2,3})$/,
		errorMessage: 'The field must contain 2 or 3 characters.',
	},
	eutraCellId: {
		regex: /^(?!0)\d{1,9}$/,
		errorMessage:
			'The field must contain only numbers and its value less than 268435455.',
	},
	nrCellId: {
		regex: /^[A-Fa-f0-9]{9}$/g,
		errorMessage: 'The field must contain 9 hexadecimal characters.',
	},
	marBwUl: {
		regex: bitRatePattern,
		errorMessage:
			'The field is invalid, only the following formats are allowed, for example: 128 bps|Kbps|Mbps|Gbps|Tbps.',
	},
	marBwDl: {
		regex: bitRatePattern,
		errorMessage:
			'The field is invalid, only the following formats are allowed, for example: 128 bps|Kbps|Mbps|Gbps|Tbps.',
	},
	mirBwUl: {
		regex: bitRatePattern,
		errorMessage:
			'The field is invalid, only the following formats are allowed, for example: 128 bps|Kbps|Mbps|Gbps|Tbps.',
	},
	mirBwDl: {
		regex: bitRatePattern,
		errorMessage:
			'The field is invalid, only the following formats are allowed, for example: 128 bps|Kbps|Mbps|Gbps|Tbps.',
	},
	rrBw: {
		regex: bitRatePattern,
		errorMessage:
			'The field is invalid, only the following formats are allowed, for example: 128 bps|Kbps|Mbps|Gbps|Tbps.',
	},
	rsBw: {
		regex: bitRatePattern,
		errorMessage:
			'The field is invalid, only the following formats are allowed, for example: 128 bps|Kbps|Mbps|Gbps|Tbps.',
	},
	lac: {
		regex: /(^[A-Fa-f0-9]{4}$)/,
		errorMessage: 'The field must contain 4 hexadecimal characters.',
	},
	sac: {
		regex: /(^[A-Fa-f0-9]{4}$)/,
		errorMessage: 'The field must contain 4 hexadecimal characters.',
	},
};

type IndexedValidations = Record<number, boolean>;

type IndexedField =
	| 'marBwUl'
	| 'marBwDl'
	| 'mirBwUl'
	| 'mirBwDl'
	| 'rrBw'
	| 'rsBw'
	| 'tac'
	| 'lac'
	| 'deviceTac'
	| 'deviceModel'
	| 'eutraCellId'
	| 'pei'
	| 'area'
	| 'anGwAddress';

interface IIsValidTaiItem {
	mcc: boolean;
	mnc: boolean;
	tac: boolean;
}

interface IIsValidECGIItem {
	mcc: boolean;
	mnc: boolean;
	eutraCellId: boolean;
}

interface IIsValidSAIItem {
	mcc: boolean;
	mnc: boolean;
	lac: boolean;
	sac: boolean;
}

interface IIsValid {
	pei: IndexedValidations;
	tac: IndexedValidations;
	lac: IndexedValidations;
	sac: boolean;
	area: IndexedValidations;
	anGwAddress: IndexedValidations;
	deviceTac: IndexedValidations;
	deviceModel: IndexedValidations;
	eutraCellId: IndexedValidations;
	tai: IIsValidTaiItem[];
	sai: IIsValidSAIItem[];
	ecgi: IIsValidECGIItem[];
	ncgi: {
		mcc: boolean;
		mnc: boolean;
		nrCellId: boolean;
	};
	marBwUl: IndexedValidations;
	marBwDl: IndexedValidations;
	mirBwUl: IndexedValidations;
	mirBwDl: IndexedValidations;
	rrBw: IndexedValidations;
	rsBw: IndexedValidations;
}

interface SelectionItemProps {
	formData: IConditionForm;
	updateFormData: (
		keyForm: string,
		path: string[],
		value: string,
		type: string,
		checked: boolean,
		key: number | null,
		data: string[] | undefined,
	) => void;
	updateNotChecked: (id: string, path: string[], key?: number | null) => void;
	onObserver: (isValid: boolean) => void;
	onAddField: (id: string, field: keyof IConditionForm) => void;
	onRemoveField: (
		id: string,
		field: keyof IConditionForm,
		index: number,
	) => void;
}

const CustomSelectionItemCondition = ({
	formData,
	updateFormData,
	updateNotChecked,
	onObserver,
	onAddField,
	onRemoveField,
}: SelectionItemProps) => {
	const [plans, setPlans] = useState<string[]>([]);

	const [getDataPlanTemplate] = useLazyGetAllPlansQuery();

	const isIndexedField = (name: string): name is IndexedField =>
		[
			'marBwUl',
			'marBwDl',
			'mirBwUl',
			'mirBwDl',
			'rrBw',
			'rsBw',
			'tac',
			'lac',
			'pei',
			'eutraCellId',
			'deviceTac',
			'deviceModel',
			'area',
			'anGwAddress',
		].includes(name);

	useEffect(() => {
		const fetchPlans = async () => {
			try {
				const response = await getDataPlanTemplate('').unwrap();
				if (response) {
					const listPlans: DataPlanTemplate[] = Array.isArray(response)
						? response
						: [response];
					const planNames = listPlans
						.map(plan => plan.name)
						.filter(name => name !== null) as string[];

					if (planNames && planNames.length > 0) {
						planNames.sort((a, b) => a.localeCompare(b));
					}
					setPlans(planNames);
				}
			} catch (error) {
				console.error('Error fetching data plans:', error);
			}
		};
		void fetchPlans();
	}, [getDataPlanTemplate]);

	const [isValid, setIsValid] = useState<IIsValid>({
		pei: {},
		tac: {},
		lac: {},
		sac: true,
		area: {},
		anGwAddress: {},
		deviceTac: {},
		deviceModel: {},
		eutraCellId: {},
		tai: [
			{
				mcc: true,
				mnc: true,
				tac: true,
			},
		],
		sai: [
			{
				mcc: true,
				mnc: true,
				lac: true,
				sac: true,
			},
		],
		ecgi: [
			{
				mcc: true,
				mnc: true,
				eutraCellId: true,
			},
		],
		ncgi: {
			mcc: true,
			mnc: true,
			nrCellId: true,
		},
		marBwUl: {},
		marBwDl: {},
		mirBwUl: {},
		mirBwDl: {},
		rrBw: {},
		rsBw: {},
	});

	const handleSelect = (path: string[], selectedOption: string) => {
		updateFormData(
			formData.id,
			path,
			selectedOption,
			'text',
			false,
			null,
			undefined,
		);
	};

	/**
	 * The function handles changes in form input fields, updating state based on the type of input.
	 * @param e - The parameter "e" is of type ChangeEvent<HTMLInputElement>, which is an event object
	 * that is triggered when the value of an input element changes. It contains information about the
	 * input element that triggered the event, such as its name, value, type, and whether it is checked
	 * (if it is a
	 */
	const handleChange = (
		path: string[],
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
		key?: number | null,
		data?: string[],
	) => {
		const {value, type} = e.target;
		const checked =
			type === 'checkbox' && 'checked' in e.target
				? (e.target as HTMLInputElement).checked
				: false;
		updateFormData(formData.id, path, value, type, checked, key ?? null, data);
	};

	/**
	 * The function `optionsRemoveField` returns an object with properties for a confirmation modal
	 * dialog, including a title, message, buttons, and various configuration options.
	 */
	const optionsRemoveField = (
		id: string,
		path: keyof IConditionForm,
		index: number,
		nameOption: string,
	) => ({
		title: `Do you really want to remove ${nameOption} #${index + 1}?`,
		message: 'The action must be saved for the change to persist.',
		buttons: [
			{
				label: 'Yes',
				onClick: () => {
					try {
						onRemoveField(id, path, index);
						toast.success('Deleted Successfully!');
					} catch (error) {
						console.error(error);
					}
				},
			},
			{
				label: 'No',
			},
		],
		closeOnEscape: true,
		closeOnClickOutside: true,
		keyCodeForClose: [8, 32],
		overlayClassName: 'overlay-custom-class-name',
	});

	const handleRemoveField = (
		id: string,
		path: keyof IConditionForm,
		index: number,
		nameOption: string,
	) => {
		confirmAlert(optionsRemoveField(id, path, index, nameOption));
	};

	const isFieldValid = (
		active: boolean,
		value: string,
		regex: RegExp,
	): boolean => {
		// Si el campo no está activo, no es necesario validarlo
		if (!active) return true;
		// Si el campo está vacío, no lo marcamos inválido de inmediato
		if (value === '' || value === undefined || value === null) return true;
		// Solo si tiene valor, lo validamos contra el regex
		return regex.test(value);
	};

	return (
		<>
			{/* 1 row */}
			<div
				className="text-bg-light mb-2 &gt; border rounded-3 px-4"
				style={{height: '100%', overflowY: 'auto'}}>
				<div className="centered-div mb-2">
					<label htmlFor="filter-list-group" className="centered-label">
						Labels selected
					</label>
				</div>
				{(formData.dnn?.[0]?.active ||
					formData.accessType?.[0]?.active ||
					formData.ratType?.[0]?.active) && (
					<div className="row">
						{formData.dnn?.[0]?.active && (
							<FieldSetSumma title={'Dnn'} key={'keydnn'}>
								<div className="row">
									{formData?.dnn?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Dnn #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['dnn'], index)
													}
												/>
												<Form.Control
													name="nameDnn"
													aria-label="nameDnnVar"
													value={item?.value}
													required
													placeholder="Dnn"
													onChange={e => handleChange(['dnn'], e, index)}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'dnn')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Dnn #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'dnn',
																	index,
																	'Dnn',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.accessType?.[0]?.active && (
							<FieldSetSumma title={'Access Type'} key={'keyacesstype'}>
								<div className="row">
									{formData?.accessType?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Access Type #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['accessType'], index)
													}
												/>
												<Form.Select
													name="accessType"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['accessType'], e, index)}
													aria-label="accessType">
													<option />
													<option value="_3GPP_ACCESS">3GPP_ACCESS</option>
													<option value="_NON_3GPP_ACCESS">
														NON_3GPP_ACCESS
													</option>
												</Form.Select>
												{index === 0 && (
													<Button
														onClick={() =>
															onAddField(formData.id, 'accessType')
														}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Access Type #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'accessType',
																	index,
																	'Access Type',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.ratType?.[0]?.active && (
							<FieldSetSumma title={'RAT Type'} key={'keyratType'}>
								<div className="row">
									{formData?.ratType?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`RAT Type #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['ratType'], index)
													}
												/>
												<Form.Select
													name="ratType"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['ratType'], e, index)}
													aria-label="ratType">
													<option />
													<option value="NR">NR</option>
													<option value="EUTRA">EUTRA</option>
													<option value="WLAN">WLAN</option>
													<option value="VIRTUAL">VIRTUAL</option>
													<option value="NBIOT">NBIOT</option>
													<option value="WIRELINE">WIRELINE</option>
													<option value="WIRELINE_CABLE">WIRELINE_CABLE</option>
													<option value="WIRELINE_BBF">WIRELINE_BBF</option>
													<option value="LTE_M">LTE_M</option>
													<option value="NR_U">NR_U</option>
													<option value="EUTRA_U">EUTRA_U</option>
													<option value="TRUSTED_N3GA">TRUSTED_N3GA</option>
													<option value="GAN">GAN</option>
													<option value="TRUSTED_WLAN">TRUSTED_WLAN</option>
													<option value="UTRA">UTRA</option>
													<option value="HSPA">HSPA</option>
													<option value="GERA">GERA</option>
													<option value="NR_LEO">NR_LEO</option>
													<option value="NR_MEO">NR_MEO</option>
													<option value="NR_GEO">NR_GEO</option>
													<option value="NR_OTHER_SAT">NR_OTHER_SAT</option>
												</Form.Select>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'ratType')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete RAT Type #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'ratType',
																	index,
																	'RAT Type',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}
					</div>
				)}
				{/* end 1 row*/}

				{/* row */}
				{(formData.online?.active ||
					formData.offline?.active ||
					formData.chargingCharacteristics?.[0]?.active) && (
					<div className="row">
						<div className="col-lg-12 bg-gray-400 rounded pt-1 pb-1 mt-2 mb-2 text-center">
							<b>Charging</b>
						</div>
						{formData.online?.active && (
							<div className="col-xs-6 col-lg-4">
								<label htmlFor="idCondOnline" className="font-weight-bold">
									Online
								</label>
								<InputGroup className="mb-3">
									<Form.Check
										name="online"
										checked={formData?.online?.value}
										type="switch"
										data-switch="true"
										id="custom-switch-charging"
										className="custom-switch-lg custom-switch-position-relative"
										onChange={e => handleChange(['online'], e)}
									/>
								</InputGroup>
							</div>
						)}
						{formData.offline?.active && (
							<div className="col-xs-6 col-lg-4">
								<label htmlFor="idCondOffline" className="font-weight-bold">
									Offline
								</label>
								<InputGroup className="mb-3">
									<Form.Check
										name="offline"
										checked={formData?.offline?.value}
										type="switch"
										id="custom-switch-charging"
										className="custom-switch-lg custom-switch-position-relative"
										onChange={e => handleChange(['offline'], e)}
									/>
								</InputGroup>
							</div>
						)}
						{formData.chargingCharacteristics?.[0]?.active && (
							<FieldSetSumma
								title={'Charging characteristics'}
								key={'keychargingCharacteristics'}>
								<div className="row">
									{formData?.chargingCharacteristics?.map(
										(item: any, index: number) => (
											<div className="col-xs-12 col-lg-6" key={item.uuid}>
												<label htmlFor="">
													{`Charging characteristics #${index + 1}`}
												</label>
												<InputGroup className="mb-3">
													<InputGroup.Checkbox
														title="Negate"
														checked={item.notChecked}
														onChange={() =>
															updateNotChecked(
																formData.id,
																['chargingCharacteristics'],
																index,
															)
														}
													/>
													<Form.Control
														name="chargingCharacteristics"
														value={item?.value}
														required={item?.active}
														onChange={e =>
															handleChange(
																['chargingCharacteristics'],
																e,
																index,
															)
														}
														aria-label="chargingCharacteristics"
													/>
													{index === 0 && (
														<Button
															onClick={() =>
																onAddField(
																	formData.id,
																	'chargingCharacteristics',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-success">
																+
															</span>
														</Button>
													)}
													{index !== 0 && (
														<OverlayTrigger
															overlay={
																<Tooltip id="tooltip-disabled">
																	{`Delete Charging characteristics #${
																		index + 1
																	}`}
																</Tooltip>
															}>
															<Button
																onClick={() =>
																	handleRemoveField(
																		formData.id,
																		'chargingCharacteristics',
																		index,
																		'Charging characteristics',
																	)
																}
																variant="outline-secondary">
																<span className="font-weight-bold text-danger">
																	-
																</span>
															</Button>
														</OverlayTrigger>
													)}
												</InputGroup>
											</div>
										),
									)}
								</div>
							</FieldSetSumma>
						)}
					</div>
				)}
				{/* end 1 row*/}

				{/* row */}
				{(formData.servingNetwork?.[0]?.mcc?.active ||
					formData.servingNetwork?.[0]?.mnc?.active ||
					formData.roaming?.[0]?.active) && (
					<div className="row">
						<div className="col-lg-12 bg-gray-400 rounded pt-1 pb-1 mt-2 mb-2 text-center">
							<b>Roaming</b>
						</div>
						{(formData.servingNetwork?.[0]?.mcc?.active ||
							formData.servingNetwork?.[0]?.mnc?.active) && (
							<FieldSetSumma
								title={'Serving Network'}
								key={'keyservingNetwork'}>
								<div className="row">
									{formData?.servingNetwork?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-3" key={item.uuid}>
											<label htmlFor="">
												{`Serving Network #${index + 1}`}
											</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={
														item?.mnc?.notChecked || item?.mcc?.notChecked
													}
													onChange={() =>
														updateNotChecked(
															formData.id,
															['servingNetwork'],
															index,
														)
													}
												/>
												<Form.Control
													type="text"
													pattern="^[0-9]{3}$"
													maxLength={3}
													name="mcc"
													value={item?.mcc?.value}
													required={item?.mcc?.active}
													onChange={e =>
														handleChange(['servingNetwork', 'mcc'], e, index)
													}
													aria-label="mcc"
													placeholder="MCC"
												/>
												<Form.Control
													type="text"
													pattern="^[0-9]{2,3}$"
													maxLength={3}
													name="mnc"
													value={item?.mnc?.value}
													required={item?.mnc?.active}
													onChange={e =>
														handleChange(['servingNetwork', 'mnc'], e, index)
													}
													aria-label="mnc"
													placeholder="MNC"
												/>
												{index === 0 && (
													<Button
														onClick={() =>
															onAddField(formData.id, 'servingNetwork')
														}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Serving Network #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'servingNetwork',
																	index,
																	'Serving Network',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.roaming?.[0]?.active && (
							<FieldSetSumma title={'Roaming'} key={'keyRoamingk'}>
								<div className="row">
									{formData?.roaming?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Roaming #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['roaming'], index)
													}
												/>
												<Form.Select
													name="roaming"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['roaming'], e, index)}
													aria-label="roaming">
													<option />
													<option value="REST">REST</option>
													<option value="GLOBAL_ALLIANCE">
														GLOBAL_ALLIANCE
													</option>
													<option value="NON_EU">NON_EU</option>
													<option value="EU">EU</option>
													<option value="AMERICAS">AMERICAS</option>
													<option value="USA">USA</option>
												</Form.Select>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'roaming')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Roaming #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'roaming',
																	index,
																	'Roaming',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}
					</div>
				)}
				{/* end 2 row */}
				{formData.signalingPath?.active && (
					<div className="row">
						<div className="col-lg-12 bg-gray-400 rounded pt-1 pb-1 mt-2 mb-2 text-center">
							<b>Signaling path sessions</b>
						</div>
						<div className="col-xs-6 col-lg-2">
							<label htmlFor="idCondSignalingPath" className="font-weight-bold">
								Signaling Path
							</label>
							<InputGroup className="mb-3">
								<Form.Check
									name="signalingPath"
									checked={formData.signalingPath?.value}
									type="switch"
									id="custom-switch-signaling"
									className="custom-switch-lg custom-switch-position-relative"
									onChange={e => handleChange(['signalingPath'], e)}
								/>
							</InputGroup>
						</div>
					</div>
				)}
				{/* end 1 row*/}

				{(formData.afAppId?.[0]?.active ||
					formData.codec?.[0]?.active ||
					formData.medType?.[0]?.active ||
					formData.flowUsage?.[0]?.active ||
					formData.fStatus?.[0]?.active ||
					formData.marBwUl?.[0]?.active ||
					formData.marBwDl?.[0]?.active ||
					formData.mirBwUl?.[0]?.active ||
					formData.mirBwDl?.[0]?.active ||
					formData.rrBw?.[0]?.active ||
					formData.rsBw?.[0]?.active) && (
					<div className="row">
						<div className="col-lg-12 bg-gray-400 rounded pt-1 pb-1 mt-2 mb-2 text-center">
							<b>Non signaling path sessions</b>
						</div>
						{formData.afAppId?.[0]?.active && (
							<FieldSetSumma title={'AF Application Id'} key={'keyAfAppId'}>
								<div className="row">
									{formData?.afAppId?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`AF Application Id #${
												index + 1
											}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['afAppId'], index)
													}
												/>
												<Form.Control
													name="afAppId"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['afAppId'], e, index)}
													aria-label="afAppId"
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'afAppId')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete AF Application Id #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'afAppId',
																	index,
																	'AF Application Id',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.codec?.[0]?.active && (
							<FieldSetSumma title={'Codec'} key={'keyCodec'}>
								<div className="row">
									{formData?.codec?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Codec #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['codec'], index)
													}
												/>
												<Form.Control
													name="codec"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['codec'], e, index)}
													aria-label="codec"
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'codec')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Codec #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'codec',
																	index,
																	'Codec',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.medType?.[0]?.active && (
							<FieldSetSumma title={'Media Type'} key={'keymedType'}>
								<div className="row">
									{formData?.medType?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Media Type #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['medType'], index)
													}
												/>
												<Form.Select
													name="medType"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['medType'], e, index)}
													aria-label="medType">
													<option />
													<option value="AUDIO">AUDIO</option>
													<option value="VIDEO">VIDEO</option>
													<option value="DATA">DATA</option>
													<option value="APPLICATION">APPLICATION</option>
													<option value="CONTROL">CONTROL</option>
													<option value="TEXT">TEXT</option>
													<option value="MESSAGE">MESSAGE</option>
													<option value="OTHER">OTHER</option>
												</Form.Select>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'medType')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Media Type #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'medType',
																	index,
																	'Media Type',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.flowUsage?.[0]?.active && (
							<FieldSetSumma title={'Flow Usage'} key={'keyflowUsage'}>
								<div className="row">
									{formData?.flowUsage?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Flow Usage #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['flowUsage'], index)
													}
												/>
												<Form.Select
													name="flowUsage"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['flowUsage'], e, index)}
													aria-label="flowUsage">
													<option />
													<option value="NO_INFO">NO_INFO</option>
													<option value="RTCP">RTCP</option>
													<option value="AF_SIGNALLING">AF_SIGNALLING</option>
												</Form.Select>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'flowUsage')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Flow Usage #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'flowUsage',
																	index,
																	'Flow Usage',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.fStatus?.[0]?.active && (
							<FieldSetSumma title={'Flow Status'} key={'keyfStatus'}>
								<div className="row">
									{formData?.fStatus?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Flow Status #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['fStatus'], index)
													}
												/>
												<Form.Select
													name="fStatus"
													value={item.value}
													required={item.active}
													onChange={e => handleChange(['fStatus'], e, index)}
													aria-label="fStatus">
													<option />
													<option value="ENABLED_UPLINK">ENABLED_UPLINK</option>
													<option value="ENABLED_DOWNLINK">
														ENABLED_DOWNLINK
													</option>
													<option value="ENABLED">ENABLED</option>
													<option value="DISABLED">DISABLED</option>
													<option value="REMOVED">REMOVED</option>
												</Form.Select>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'fStatus')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Flow Status #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'fStatus',
																	index,
																	'Flow Status',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.marBwUl?.[0]?.active && (
							<FieldSetSumma
								title={'Max Requested Bandwidth Upload'}
								key={'keymarBwUl'}>
								<div className="row">
									{formData?.marBwUl?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`MarBwUl #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['marBwUl'], index)
													}
												/>
												<Form.Control
													name="marBwUl"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['marBwUl'], e, index)}
													onInput={(e: ChangeEvent<HTMLInputElement>) => {
														const {name} = e.target;
														const target =
															e.target as unknown as HTMLInputElement;
														const val = target.value.slice(0, 10);
														target.value = val;
														const rule = validationRules[name as never] as any;
														const result = rule.regex.test(val);
														if (isIndexedField(name)) {
															setIsValid(prev => ({
																...prev,
																[name]: {
																	...(prev[name] ?? {}),
																	[index]: result,
																},
															}));
														}
														onObserver(result);
													}}
													aria-label="marBwUl"
													placeholder="E.g. 128 Kbps"
													className={
														isIndexedField('marBwUl') &&
														isValid.marBwUl?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'marBwUl')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Max Requested Bandwidth Upload #${
																	index + 1
																}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'marBwUl',
																	index,
																	'Max Requested Bandwidth Upload',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('marBwUl') &&
													isValid.marBwUl?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.marBwUl.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.marBwDl?.[0]?.active && (
							<FieldSetSumma
								title={'Max Requested Bandwidth Download'}
								key={'keymarBwDl'}>
								<div className="row">
									{formData?.marBwDl?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`MarBwDl #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['marBwDl'], index)
													}
												/>
												<Form.Control
													name="marBwDl"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['marBwDl'], e, index)}
													onInput={(e: ChangeEvent<HTMLInputElement>) => {
														const {name} = e.target;
														const target =
															e.target as unknown as HTMLInputElement;
														const val = target.value.slice(0, 10);
														target.value = val;
														const rule = validationRules[name as never] as any;
														const result = rule.regex.test(val);
														if (isIndexedField(name)) {
															setIsValid(prev => ({
																...prev,
																[name]: {
																	...(prev[name] ?? {}),
																	[index]: result,
																},
															}));
														}
														onObserver(result);
													}}
													aria-label="marBwDl"
													placeholder="E.g. 128 Kbps"
													className={
														isIndexedField('marBwDl') &&
														isValid.marBwDl?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'marBwDl')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Max Requested Bandwidth Download #${
																	index + 1
																}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'marBwDl',
																	index,
																	'Max Requested Bandwidth Download',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('marBwDl') &&
													isValid.marBwDl?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.marBwDl.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.mirBwUl?.[0]?.active && (
							<FieldSetSumma
								title={'Min requested Bandwidth Upload'}
								key={'keymirBwUl'}>
								<div className="row">
									{formData?.mirBwUl?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`MirBwUl #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['mirBwUl'], index)
													}
												/>
												<Form.Control
													name="mirBwUl"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['mirBwUl'], e, index)}
													onInput={(e: ChangeEvent<HTMLInputElement>) => {
														const {name} = e.target;
														const target =
															e.target as unknown as HTMLInputElement;
														const val = target.value.slice(0, 10);
														target.value = val;
														const rule = validationRules[name as never] as any;
														const result = rule.regex.test(val);
														if (isIndexedField(name)) {
															setIsValid(prev => ({
																...prev,
																[name]: {
																	...(prev[name] ?? {}),
																	[index]: result,
																},
															}));
														}
														onObserver(result);
													}}
													aria-label="mirBwUl"
													placeholder="E.g. 128 Kbps"
													className={
														isIndexedField('mirBwUl') &&
														isValid.mirBwUl?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'mirBwUl')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Min requested Bandwidth Upload #${
																	index + 1
																}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'mirBwUl',
																	index,
																	'Min requested Bandwidth Upload',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('mirBwUl') &&
													isValid.mirBwUl?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.mirBwUl.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.mirBwDl?.[0]?.active && (
							<FieldSetSumma
								title={'Min Requested Bandwidth Download'}
								key={'keymirBwDl'}>
								<div className="row">
									{formData?.mirBwDl?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`MirBwDl #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['mirBwDl'], index)
													}
												/>
												<Form.Control
													name="mirBwDl"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['mirBwDl'], e, index)}
													onInput={(e: ChangeEvent<HTMLInputElement>) => {
														const {name} = e.target;
														const target =
															e.target as unknown as HTMLInputElement;
														const val = target.value.slice(0, 10);
														target.value = val;
														const rule = validationRules[name as never] as any;
														const result = rule.regex.test(val);
														if (isIndexedField(name)) {
															setIsValid(prev => ({
																...prev,
																[name]: {
																	...(prev[name] ?? {}),
																	[index]: result,
																},
															}));
														}
														onObserver(result);
													}}
													aria-label="mirBwDl"
													placeholder="E.g. 128 Kbps"
													className={
														isIndexedField('mirBwDl') &&
														isValid.mirBwDl?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'mirBwDl')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Min Requested Bandwidth Download #${
																	index + 1
																}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'mirBwDl',
																	index,
																	'Min Requested Bandwidth Download',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('mirBwDl') &&
													isValid.mirBwDl?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.mirBwDl.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.rrBw?.[0]?.active && (
							<FieldSetSumma title={'RR Bw'} key={'keyrrBw'}>
								<div className="row">
									{formData?.rrBw?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`RrBw #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['rrBw'], index)
													}
												/>
												<Form.Control
													name="rrBw"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['rrBw'], e, index)}
													onInput={(e: ChangeEvent<HTMLInputElement>) => {
														const {name} = e.target;
														const target =
															e.target as unknown as HTMLInputElement;
														const val = target.value.slice(0, 10);
														target.value = val;
														const rule = validationRules[name as never] as any;
														const result = rule.regex.test(val);
														if (isIndexedField(name)) {
															setIsValid(prev => ({
																...prev,
																[name]: {
																	...(prev[name] ?? {}),
																	[index]: result,
																},
															}));
														}
														onObserver(result);
													}}
													aria-label="rrBw"
													placeholder="E.g. 128 Kbps"
													className={
														isIndexedField('rrBw') &&
														isValid.rrBw?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'rrBw')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete RR Bw #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'rrBw',
																	index,
																	'RR Bw',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('rrBw') &&
													isValid.rrBw?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.rrBw.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.rsBw?.[0]?.active && (
							<FieldSetSumma title={'RS Bw'} key={'keyrsBw'}>
								<div className="row">
									{formData?.rsBw?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`RsBw #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['rsBw'], index)
													}
												/>
												<Form.Control
													name="rsBw"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['rsBw'], e, index)}
													onInput={(e: ChangeEvent<HTMLInputElement>) => {
														const {name} = e.target;
														const target =
															e.target as unknown as HTMLInputElement;
														const val = target.value.slice(0, 10);
														target.value = val;
														const rule = validationRules[name as never] as any;
														const result = rule.regex.test(val);
														if (isIndexedField(name)) {
															setIsValid(prev => ({
																...prev,
																[name]: {
																	...(prev[name] ?? {}),
																	[index]: result,
																},
															}));
														}
														onObserver(result);
													}}
													aria-label="rsBw"
													placeholder="E.g. 128 Kbps"
													className={
														isIndexedField('rsBw') &&
														isValid.rsBw?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'rsBw')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete RS Bw #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'rsBw',
																	index,
																	'RS Bw',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('rsBw') &&
													isValid.rsBw?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.rsBw.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}
					</div>
				)}
				{/* end 11 row*/}

				{/* 1 row */}
				{formData.servUrn?.[0]?.active && (
					<div className="row">
						<div className="col-lg-12 bg-gray-400 rounded pt-1 pb-1 mt-2 mb-2 text-center">
							<b>Emergency Traffic</b>
						</div>
						<FieldSetSumma title={'Service URN'} key={'keyservUrn'}>
							<div className="row">
								{formData?.servUrn?.map((item: any, index: number) => (
									<div className="col-xs-12 col-lg-6" key={item.uuid}>
										<label htmlFor="">{`Service URN #${index + 1}`}</label>
										<InputGroup className="mb-3">
											<InputGroup.Checkbox
												title="Negate"
												checked={item?.notChecked}
												onChange={() =>
													updateNotChecked(formData.id, ['servUrn'], index)
												}
											/>
											<Form.Control
												name="servUrn"
												value={item?.value}
												required={item?.active}
												onChange={e => handleChange(['servUrn'], e, index)}
												aria-label="servUrn"
											/>
											{index === 0 && (
												<Button
													onClick={() => onAddField(formData.id, 'servUrn')}
													variant="outline-secondary">
													<span className="font-weight-bold text-success">
														+
													</span>
												</Button>
											)}
											{index !== 0 && (
												<OverlayTrigger
													overlay={
														<Tooltip id="tooltip-disabled">
															{`Delete Service URN #${index + 1}`}
														</Tooltip>
													}>
													<Button
														onClick={() =>
															handleRemoveField(
																formData.id,
																'servUrn',
																index,
																'Service URN',
															)
														}
														variant="outline-secondary">
														<span className="font-weight-bold text-danger">
															-
														</span>
													</Button>
												</OverlayTrigger>
											)}
										</InputGroup>
									</div>
								))}
							</div>
						</FieldSetSumma>
					</div>
				)}
				{/* end 1 row */}

				{/* only display plan DATA */}
				{/* 6 row */}
				{(formData.planType?.active ||
					formData.usageMonitoring?.active ||
					formData.finalAction?.active ||
					formData.homeZone?.active ||
					formData.category?.[0]?.active) && (
					<div className="row">
						<div className="col-lg-12 bg-gray-400 rounded pt-1 pb-1 mt-2 mb-2 text-center">
							<b>Data</b>
						</div>
						{formData.planType?.active && (
							<div className="col-xs-12 col-lg-6">
								<label htmlFor="idCondiPlanType">Plan Type</label>
								<InputGroup className="mb-3">
									<DropdownSumma
										options={plans}
										onSelect={handleSelect}
										selectedValue={formData.planType?.value}
										requiredValue={formData.planType?.active}
										nameDropdown={'planType'}
									/>
								</InputGroup>
							</div>
						)}

						{formData.finalAction?.active && (
							<div className="col-xs-12 col-lg-3">
								<label htmlFor="idCondiFinalAction">Final Action</label>
								<InputGroup className="mb-3">
									<Form.Check
										name="finalAction"
										checked={formData?.finalAction?.value}
										required={formData?.finalAction?.active}
										type="switch"
										id="custom-switch-finalAction"
										className="custom-switch-lg custom-switch-position-relative"
										onChange={e => handleChange(['finalAction'], e)}
									/>
								</InputGroup>
							</div>
						)}

						{formData.homeZone?.active && (
							<div className="col-xs-12 col-lg-3">
								<label htmlFor="idCondiHomeZone">Home Zone</label>
								<InputGroup className="mb-3">
									<Form.Check
										name="homeZone"
										checked={formData?.homeZone?.value}
										required={formData?.homeZone?.active}
										type="switch"
										id="custom-switch-homezone"
										className="custom-switch-lg custom-switch-position-relative"
										onChange={e => handleChange(['homeZone'], e)}
									/>
								</InputGroup>
							</div>
						)}

						{formData.usageMonitoring?.active && (
							<div className="col-xs-12 col-lg-6">
								<label htmlFor="idCondUsageMonitoring">Usage Monitoring</label>
								<InputGroup className="mb-3">
									<Form.Control
										name="usageMonitoring"
										value={formData.usageMonitoring.value}
										required={formData.usageMonitoring.active}
										onChange={e => handleChange(['usageMonitoring'], e)}
										aria-label="usageMonitoring"
									/>
								</InputGroup>
							</div>
						)}

						{formData.category?.[0]?.active && (
							<FieldSetSumma title={'Category'} key={'keycategory'}>
								<div className="row">
									{formData?.category?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Category #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['category'], index)
													}
												/>
												<Form.Control
													name="category"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['category'], e, index)}
													aria-label="category"
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'category')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Category #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'category',
																	index,
																	'Category',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}
					</div>
				)}
				{/* end 5 row */}

				{/* 1 row */}
				{formData.time?.[0]?.active && (
					<div className="row">
						<div className="col-lg-12 bg-gray-400 rounded pt-1 pb-1 mt-2 mb-2 text-center">
							<b>Time Policies</b>
						</div>
						<FieldSetSumma title={'Time'} key={'keytime'}>
							<div className="row">
								{formData?.time?.map((item: any, index: number) => (
									<div className="col-xs-12 col-lg-6" key={item.uuid}>
										<label htmlFor="">{`Time #${index + 1}`}</label>
										<InputGroup className="mb-3">
											<Form.Control
												name="time"
												value={item?.value}
												required={item?.active}
												onChange={e => handleChange(['time'], e, index)}
												aria-label="time"
											/>
											{index === 0 && (
												<Button
													onClick={() => onAddField(formData.id, 'time')}
													variant="outline-secondary">
													<span className="font-weight-bold text-success">
														+
													</span>
												</Button>
											)}
											{index !== 0 && (
												<OverlayTrigger
													overlay={
														<Tooltip id="tooltip-disabled">
															{`Delete Time #${index + 1}`}
														</Tooltip>
													}>
													<Button
														onClick={() =>
															handleRemoveField(
																formData.id,
																'time',
																index,
																'Time',
															)
														}
														variant="outline-secondary">
														<span className="font-weight-bold text-danger">
															-
														</span>
													</Button>
												</OverlayTrigger>
											)}
										</InputGroup>
									</div>
								))}
							</div>
						</FieldSetSumma>
					</div>
				)}
				{/* end 1 row */}

				{/* 1 row */}
				{formData.pei?.[0]?.active && (
					<div className="row">
						<div className="col-lg-12 bg-gray-400 rounded pt-1 pb-1 mt-2 mb-2 text-center">
							<b>User Equipment Info</b>
						</div>
						<FieldSetSumma title={'PEI'} key={'keypei'}>
							<div className="row">
								{formData?.pei?.map((item: any, index: number) => (
									<div className="col-xs-12 col-lg-10" key={item.uuid}>
										<label htmlFor="">{`PEI #${index + 1}`}</label>
										<InputGroup className="mb-3">
											<InputGroup.Checkbox
												title="Negate"
												checked={item?.notChecked}
												onChange={() =>
													updateNotChecked(formData.id, ['pei'], index)
												}
											/>
											<Form.Control
												name="pei"
												value={item?.value}
												required={item?.active}
												onChange={e => handleChange(['pei'], e, index)}
												aria-label="pei"
												className={!isValid.pei ? 'is-invalid' : ''}
											/>
											{index === 0 && (
												<Button
													onClick={() => onAddField(formData.id, 'pei')}
													variant="outline-secondary">
													<span className="font-weight-bold text-success">
														+
													</span>
												</Button>
											)}
											{index !== 0 && (
												<OverlayTrigger
													overlay={
														<Tooltip id="tooltip-disabled">
															{`Delete PEI #${index + 1}`}
														</Tooltip>
													}>
													<Button
														onClick={() =>
															handleRemoveField(
																formData.id,
																'pei',
																index,
																'PEI',
															)
														}
														variant="outline-secondary">
														<span className="font-weight-bold text-danger">
															-
														</span>
													</Button>
												</OverlayTrigger>
											)}
										</InputGroup>
									</div>
								))}
							</div>
						</FieldSetSumma>
					</div>
				)}
				{/* end 1 row*/}

				{/* 1 row */}
				{(formData?.tac?.[0]?.active ||
					formData?.eutraCellId?.[0]?.active ||
					formData?.area?.[0]?.active ||
					formData?.lac?.[0]?.active ||
					formData?.anGwAddress?.[0]?.active ||
					formData?.tai?.[0]?.tac?.active ||
					formData?.tai?.[0]?.plmnId?.mnc?.active ||
					formData?.tai?.[0]?.plmnId?.mcc?.active ||
					formData.ecgi?.[0]?.eutraCellId?.active ||
					formData?.ecgi?.[0]?.plmnId?.mnc?.active ||
					formData?.ecgi?.[0]?.plmnId?.mcc?.active ||
					formData.sai?.[0]?.lac?.active ||
					formData.sai?.[0]?.sac?.active ||
					formData?.sai?.[0]?.plmnId?.mnc?.active ||
					formData?.sai?.[0]?.plmnId?.mcc?.active) && (
					<div className="row">
						<div className="col-lg-12 bg-gray-400 rounded pt-1 pb-1 mt-2 mb-2 text-center">
							<b>Location</b>
						</div>
						{formData.tac?.[0]?.active && (
							<FieldSetSumma title={'TAC'} key={'keytac'}>
								<div className="row">
									{formData?.tac?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`TAC #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['tac'], index)
													}
												/>
												<Form.Control
													name="tac"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['tac'], e, index)}
													onInput={(e: ChangeEvent<HTMLInputElement>) => {
														const {name} = e.target;
														const target =
															e.target as unknown as HTMLInputElement;
														const val = target.value.slice(0, 4);
														target.value = val;
														const rule = validationRules[name as never] as any;
														const result = rule.regex.test(val);
														if (isIndexedField(name)) {
															setIsValid(prev => ({
																...prev,
																[name]: {
																	...(prev[name] ?? {}),
																	[index]: result,
																},
															}));
														}
														onObserver(result);
													}}
													aria-label="tac"
													className={
														isIndexedField('tac') &&
														isValid.tac?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'tac')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete TAC #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'tac',
																	index,
																	'TAC',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('tac') &&
													isValid.tac?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.tac.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData?.eutraCellId?.[0]?.active && (
							<FieldSetSumma title={'EUTRA Cell Id'} key={'keyeutraCellId'}>
								<div className="row">
									{formData?.eutraCellId?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`EUTRA Cell Id #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(
															formData.id,
															['eutraCellId'],
															index,
														)
													}
												/>
												<Form.Control
													name="eutraCellId"
													value={item?.value}
													required={item?.active}
													onChange={e =>
														handleChange(['eutraCellId'], e, index)
													}
													onInput={(e: ChangeEvent<HTMLInputElement>) => {
														const {name} = e.target;
														const target =
															e.target as unknown as HTMLInputElement;
														const val = target.value.slice(0, 7);
														target.value = val;
														const rule = validationRules[name as never] as any;
														const result = rule.regex.test(val);
														if (isIndexedField(name)) {
															setIsValid(prev => ({
																...prev,
																[name]: {
																	...(prev[name] ?? {}),
																	[index]: result,
																},
															}));
														}
														onObserver(result);
													}}
													aria-label="eutraCellId"
													className={!isValid.eutraCellId ? 'is-invalid' : ''}
												/>
												{index === 0 && (
													<Button
														onClick={() =>
															onAddField(formData.id, 'eutraCellId')
														}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete EUTRA Cell Id #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'eutraCellId',
																	index,
																	'EUTRA Cell Id',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('eutraCellId') &&
													isValid.eutraCellId?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.eutraCellId.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData?.area?.[0]?.active && (
							<FieldSetSumma title={'Area'} key={'keyarea'}>
								<div className="row">
									{formData?.area?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Area #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['area'], index)
													}
												/>
												<Form.Control
													name="area"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['area'], e, index)}
													aria-label="area"
													className={!isValid.area ? 'is-invalid' : ''}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'area')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Area #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'area',
																	index,
																	'Area',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('area') &&
													isValid.area?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.area.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData?.lac?.[0]?.active && (
							<FieldSetSumma title={'LAC'} key={'keylac'}>
								<div className="row">
									{formData?.lac?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`LAC #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['lac'], index)
													}
												/>
												<Form.Control
													name="lac"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['lac'], e, index)}
													onInput={(e: ChangeEvent<HTMLInputElement>) => {
														const {name} = e.target;
														const target =
															e.target as unknown as HTMLInputElement;
														const val = target.value.slice(0, 4);
														target.value = val;
														const rule = validationRules[name as never] as any;
														const result = rule.regex.test(val);
														if (isIndexedField(name)) {
															setIsValid(prev => ({
																...prev,
																[name]: {
																	...(prev[name] ?? {}),
																	[index]: result,
																},
															}));
														}
														onObserver(result);
													}}
													aria-label="lac"
													className={
														isIndexedField('lac') &&
														isValid.lac?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'lac')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete LAC #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'lac',
																	index,
																	'LAC',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('lac') &&
													isValid.lac?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.lac.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData?.anGwAddress?.[0]?.active && (
							<FieldSetSumma title={'AN GW Address'} key={'keyanGwAddress'}>
								<div className="row">
									{formData?.anGwAddress?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`AN GW Address #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(
															formData.id,
															['anGwAddress'],
															index,
														)
													}
												/>
												<Form.Control
													name="anGwAddress"
													value={item?.value}
													required={item?.active}
													onChange={e =>
														handleChange(['anGwAddress'], e, index)
													}
													aria-label="anGwAddress"
													className={
														isIndexedField('anGwAddress') &&
														isValid.anGwAddress?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() =>
															onAddField(formData.id, 'anGwAddress')
														}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete AN GW Address #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'anGwAddress',
																	index,
																	'AN GW Address',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('anGwAddress') &&
													isValid.anGwAddress?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.anGwAddress.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{(formData?.tai?.[0]?.tac?.active ||
							formData?.tai?.[0]?.plmnId?.mnc?.active ||
							formData?.tai?.[0]?.plmnId?.mcc?.active) && (
							<FieldSetSumma title={'TAI'} key={'keytai'}>
								<div className="row">
									{formData?.tai?.map((item: any, index: number) => (
										<FieldSetSumma title={`TAI #${index + 1}`} key={item.uuid}>
											<InputGroup className="mb-3">
												<OverlayTrigger
													placement="right"
													overlay={<Tooltip id="tooltip-not">Negate</Tooltip>}>
													<Form.Check
														title="Not"
														name="tai"
														checked={
															item?.tac?.notChecked ||
															item?.plmnId?.mnc?.notChecked ||
															item?.plmnId?.mcc?.notChecked
														}
														type="switch"
														data-switch="true"
														id="custom-switch-tai"
														className="custom-switch-lg custom-switch-position-relative"
														onChange={() =>
															updateNotChecked(formData.id, ['tai'], index)
														}
													/>
												</OverlayTrigger>
											</InputGroup>
											{index === 0 && (
												<Button
													onClick={() => onAddField(formData.id, 'tai')}
													type="button"
													className="border-0 bg-transparent d-inline position-absolute top-0 end-0 pr-2 mt-n3"
													data-bs-toggle="tooltip"
													data-bs-placement="top"
													title="Add TAI"
													aria-expanded="true">
													<i
														className="bi-1 bi-plus-circle-fill text-success"
														title="Add"
														data-toggle="tooltip"
														data-original-title="Add"
													/>
												</Button>
											)}
											{index !== 0 && (
												<OverlayTrigger
													overlay={
														<Tooltip id="tooltip-disabled">
															{`Delete TAI #${index + 1}`}
														</Tooltip>
													}>
													<Button
														onClick={() =>
															handleRemoveField(
																formData.id,
																'tai',
																index,
																'TAI',
															)
														}
														type="button"
														className="border-0 bg-transparent d-inline position-absolute top-0 end-0 pr-2 mt-n3"
														data-bs-toggle="collapse"
														data-bs-target="#newresetTAI1"
														aria-expanded="true">
														<i
															className="bi-1 bi-trash3-fill text-danger"
															title=""
															data-toggle="tooltip"
															data-original-title="Delete"
														/>
													</Button>
												</OverlayTrigger>
											)}
											<div className="row">
												{item?.plmnId?.mnc?.active && (
													<div className="col-12 col-lg-4">
														<label htmlFor={`idCondiTAIMnc #${index + 1}`}>
															MNC
														</label>
														<InputGroup className="mb-3">
															<Form.Control
																name="mnc"
																value={item?.plmnId?.mnc?.value}
																onChange={e =>
																	handleChange(
																		['tai', 'plmnId', 'mnc'],
																		e,
																		index,
																	)
																}
																onInput={(e: ChangeEvent<HTMLInputElement>) => {
																	const {name} = e.target;
																	const target =
																		e.target as unknown as HTMLInputElement;
																	const val = target.value.slice(0, 3);
																	target.value = val;
																	const rule = validationRules[
																		name as never
																	] as any;
																	const result =
																		val === '' || rule.regex.test(val);
																	setIsValid(prev => {
																		const taiValid = [...(prev.tai || [])];
																		if (!taiValid[index])
																			taiValid[index] = {
																				mcc: true,
																				mnc: true,
																				tac: true,
																			};
																		taiValid[index] = {
																			...taiValid[index],
																			[name]: result,
																		};
																		return {...prev, tai: taiValid};
																	});
																	onObserver(result);
																}}
																className={
																	!isFieldValid(
																		item?.plmnId?.mnc?.active,
																		item?.plmnId?.mnc?.value,
																		validationRules.mnc.regex,
																	)
																		? 'is-invalid'
																		: ''
																}
																aria-label="mnc"
															/>
															{item?.plmnId?.mnc?.active &&
																item?.plmnId?.mnc?.value !== '' &&
																!isValid.tai?.[index]?.mnc && (
																	<div className="invalid-feedback">
																		{validationRules.mnc.errorMessage}
																	</div>
																)}
														</InputGroup>
													</div>
												)}

												{item?.plmnId?.mcc?.active && (
													<div className="col-12 col-lg-4">
														<label htmlFor={`idCondiTAIMcc #${index + 1}`}>
															MCC
														</label>
														<InputGroup className="mb-3">
															<Form.Control
																name="mcc"
																value={item?.plmnId?.mcc?.value}
																onChange={e =>
																	handleChange(
																		['tai', 'plmnId', 'mcc'],
																		e,
																		index,
																	)
																}
																aria-label="mcc"
																onInput={(e: ChangeEvent<HTMLInputElement>) => {
																	const {name} = e.target;
																	const target =
																		e.target as unknown as HTMLInputElement;
																	const val = target.value.slice(0, 3);
																	target.value = val;
																	const rule = validationRules[
																		name as never
																	] as any;
																	const result =
																		val === '' || rule.regex.test(val);
																	setIsValid(prev => {
																		const taiValid = [...(prev.tai || [])];
																		if (!taiValid[index])
																			taiValid[index] = {
																				mcc: true,
																				mnc: true,
																				tac: true,
																			};
																		taiValid[index] = {
																			...taiValid[index],
																			[name]: result,
																		};
																		return {...prev, tai: taiValid};
																	});
																	onObserver(result);
																}}
																className={
																	!isFieldValid(
																		item?.plmnId?.mcc?.active,
																		item?.plmnId?.mcc?.value,
																		validationRules.mcc.regex,
																	)
																		? 'is-invalid'
																		: ''
																}
															/>
															{item?.plmnId?.mcc?.active &&
																item?.plmnId?.mcc?.value !== '' &&
																!isValid.tai?.[index]?.mcc && (
																	<div className="invalid-feedback">
																		{validationRules.mcc.errorMessage}
																	</div>
																)}
														</InputGroup>
													</div>
												)}

												{item?.tac?.active && (
													<div className="col-12 col-lg-4">
														<label htmlFor={`idCondiTAITac #${index + 1}`}>
															TAC
														</label>
														<InputGroup className="mb-3">
															<Form.Control
																name="tac"
																value={item?.tac?.value}
																onChange={e =>
																	handleChange(['tai', 'tac'], e, index)
																}
																onInput={(e: ChangeEvent<HTMLInputElement>) => {
																	const {name} = e.target;
																	const target =
																		e.target as unknown as HTMLInputElement;
																	const val = target.value.slice(0, 4);
																	target.value = val;
																	const rule = validationRules[
																		name as never
																	] as any;
																	const result =
																		val === '' || rule.regex.test(val);
																	setIsValid(prev => {
																		const taiValid = [...(prev.tai || [])];
																		if (!taiValid[index])
																			taiValid[index] = {
																				mcc: true,
																				mnc: true,
																				tac: true,
																			};
																		taiValid[index] = {
																			...taiValid[index],
																			[name]: result,
																		};
																		return {...prev, tai: taiValid};
																	});
																	onObserver(result);
																}}
																aria-label="tac"
																className={
																	!isFieldValid(
																		item?.tac?.active,
																		item?.tac?.value,
																		validationRules.tac.regex,
																	)
																		? 'is-invalid'
																		: ''
																}
															/>
															{!isValid.tai?.[index]?.tac && (
																<div className="invalid-feedback">
																	{validationRules.tac.errorMessage}
																</div>
															)}
														</InputGroup>
													</div>
												)}
											</div>
										</FieldSetSumma>
									))}
								</div>
							</FieldSetSumma>
						)}

						{(formData.ecgi?.[0]?.eutraCellId?.active ||
							formData?.ecgi?.[0]?.plmnId?.mnc?.active ||
							formData?.ecgi?.[0]?.plmnId?.mcc?.active) && (
							<FieldSetSumma title={'ECGI'} key={'keyECGI'}>
								<div className="row">
									{formData?.ecgi?.map((item: any, index: number) => (
										<FieldSetSumma title={`ECGI #${index + 1}`} key={item.uuid}>
											<InputGroup className="mb-3">
												<OverlayTrigger
													placement="right"
													overlay={<Tooltip id="tooltip-not">Negate</Tooltip>}>
													<Form.Check
														title="Not"
														name="ecgi"
														checked={
															item?.eutraCellId?.notChecked ||
															item?.plmnId?.mnc?.notChecked ||
															item?.plmnId?.mcc?.notChecked
														}
														type="switch"
														data-switch="true"
														id="custom-switch-ecgi"
														className="custom-switch-lg custom-switch-position-relative"
														onChange={() =>
															updateNotChecked(formData.id, ['ecgi'], index)
														}
													/>
												</OverlayTrigger>
											</InputGroup>
											{index === 0 && (
												<Button
													onClick={() => onAddField(formData.id, 'ecgi')}
													type="button"
													className="border-0 bg-transparent d-inline position-absolute top-0 end-0 pr-2 mt-n3"
													data-bs-toggle="tooltip"
													data-bs-placement="top"
													title="Add ECGI"
													aria-expanded="true">
													<i
														className="bi-1 bi-plus-circle-fill text-success"
														title="Add"
														data-toggle="tooltip"
														data-original-title="Add"
													/>
												</Button>
											)}
											{index !== 0 && (
												<OverlayTrigger
													overlay={
														<Tooltip id="tooltip-disabled">
															{`Delete ECGI #${index + 1}`}
														</Tooltip>
													}>
													<Button
														onClick={() =>
															handleRemoveField(
																formData.id,
																'ecgi',
																index,
																'ECGI',
															)
														}
														type="button"
														className="border-0 bg-transparent d-inline position-absolute top-0 end-0 pr-2 mt-n3"
														data-bs-toggle="collapse"
														data-bs-target="#newresetECGI1"
														aria-expanded="true">
														<i
															className="bi-1 bi-trash3-fill text-danger"
															title=""
															data-toggle="tooltip"
															data-original-title="Delete"
														/>
													</Button>
												</OverlayTrigger>
											)}
											<div className="row">
												{item?.plmnId?.mnc?.active && (
													<div className="col-12 col-lg-4">
														<label htmlFor={`idCondiECGIMnc #${index + 1}`}>
															MNC
														</label>
														<InputGroup className="mb-3">
															<Form.Control
																name="mnc"
																value={item?.plmnId?.mnc?.value}
																onChange={e =>
																	handleChange(
																		['ecgi', 'plmnId', 'mnc'],
																		e,
																		index,
																	)
																}
																onInput={(e: ChangeEvent<HTMLInputElement>) => {
																	const {name} = e.target;
																	const target =
																		e.target as unknown as HTMLInputElement;
																	const val = target.value.slice(0, 3);
																	target.value = val;
																	const rule = validationRules[
																		name as never
																	] as any;
																	const result =
																		val === '' || rule.regex.test(val);
																	setIsValid(prev => {
																		const ecgiValid = [...(prev.ecgi || [])];
																		if (!ecgiValid[index])
																			ecgiValid[index] = {
																				mcc: true,
																				mnc: true,
																				eutraCellId: true,
																			};
																		ecgiValid[index] = {
																			...ecgiValid[index],
																			[name]: result,
																		};
																		return {...prev, ecgi: ecgiValid};
																	});
																	onObserver(result);
																}}
																className={
																	!isFieldValid(
																		item?.plmnId?.mnc?.active,
																		item?.plmnId?.mnc?.value,
																		validationRules.mnc.regex,
																	)
																		? 'is-invalid'
																		: ''
																}
																aria-label="mnc"
															/>
															{item?.plmnId?.mnc?.active &&
																item?.plmnId?.mnc?.value !== '' &&
																!isValid.ecgi?.[index]?.mnc && (
																	<div className="invalid-feedback">
																		{validationRules.mnc.errorMessage}
																	</div>
																)}
														</InputGroup>
													</div>
												)}

												{item?.plmnId?.mcc?.active && (
													<div className="col-12 col-lg-4">
														<label htmlFor={`idCondiECGIMcc #${index + 1}`}>
															MCC
														</label>
														<InputGroup className="mb-3">
															<Form.Control
																name="mcc"
																value={item?.plmnId?.mcc?.value}
																onChange={e =>
																	handleChange(
																		['ecgi', 'plmnId', 'mcc'],
																		e,
																		index,
																	)
																}
																onInput={(e: ChangeEvent<HTMLInputElement>) => {
																	const {name} = e.target;
																	const target =
																		e.target as unknown as HTMLInputElement;
																	const val = target.value.slice(0, 3);
																	target.value = val;
																	const rule = validationRules[
																		name as never
																	] as any;
																	const result =
																		val === '' || rule.regex.test(val);
																	setIsValid(prev => {
																		const ecgiValid = [...(prev.ecgi || [])];
																		if (!ecgiValid[index])
																			ecgiValid[index] = {
																				mcc: true,
																				mnc: true,
																				eutraCellId: true,
																			};
																		ecgiValid[index] = {
																			...ecgiValid[index],
																			[name]: result,
																		};
																		return {...prev, ecgi: ecgiValid};
																	});
																	onObserver(result);
																}}
																className={
																	!isFieldValid(
																		item?.plmnId?.mcc?.active,
																		item?.plmnId?.mcc?.value,
																		validationRules.mcc.regex,
																	)
																		? 'is-invalid'
																		: ''
																}
																aria-label="mcc"
															/>
															{item?.plmnId?.mcc?.active &&
																item?.plmnId?.mcc?.value !== '' &&
																!isValid.ecgi?.[index]?.mcc && (
																	<div className="invalid-feedback">
																		{validationRules.mcc.errorMessage}
																	</div>
																)}
														</InputGroup>
													</div>
												)}

												{item?.eutraCellId?.active && (
													<div className="col-12 col-lg-4">
														<label htmlFor={`idCondiEcgiEUTRA #${index + 1}`}>
															EUTRA Cell Id
														</label>
														<InputGroup className="mb-3">
															<Form.Control
																name="eutraCellId"
																value={item?.eutraCellId?.value}
																onChange={e =>
																	handleChange(
																		['ecgi', 'eutraCellId'],
																		e,
																		index,
																	)
																}
																aria-label="eutraCellId"
																onInput={(e: ChangeEvent<HTMLInputElement>) => {
																	const {name} = e.target;
																	const target =
																		e.target as unknown as HTMLInputElement;
																	const val = target.value.slice(0, 9);
																	target.value = val;
																	const rule = validationRules[
																		name as never
																	] as any;
																	const result =
																		rule.regex.test(val) &&
																		parseInt(val, 10) <= 268435455;
																	setIsValid(prev => {
																		const ecgiValid = [...(prev.ecgi || [])];
																		if (!ecgiValid[index])
																			ecgiValid[index] = {
																				mcc: true,
																				mnc: true,
																				eutraCellId: true,
																			};
																		ecgiValid[index] = {
																			...ecgiValid[index],
																			[name]: result,
																		};
																		return {...prev, ecgi: ecgiValid};
																	});
																	onObserver(result);
																}}
																className={
																	!isFieldValid(
																		item?.eutraCellId?.active,
																		item?.eutraCellId?.value,
																		validationRules.eutraCellId.regex,
																	)
																		? 'is-invalid'
																		: ''
																}
															/>
															{item?.eutraCellId?.active &&
																item?.eutraCellId?.value !== '' &&
																!isValid.ecgi?.[index]?.eutraCellId && (
																	<div className="invalid-feedback">
																		{validationRules.eutraCellId.errorMessage}
																	</div>
																)}
														</InputGroup>
													</div>
												)}
											</div>
										</FieldSetSumma>
									))}
								</div>
							</FieldSetSumma>
						)}

						{(formData.sai?.[0]?.lac?.active ||
							formData.sai?.[0]?.sac?.active ||
							formData?.sai?.[0]?.plmnId?.mnc?.active ||
							formData?.sai?.[0]?.plmnId?.mcc?.active) && (
							<FieldSetSumma title={'SAI'} key={'keySAI'}>
								<div className="row">
									{formData?.sai?.map((item: any, index: number) => (
										<FieldSetSumma title={`SAI #${index + 1}`} key={item.uuid}>
											<InputGroup className="mb-3">
												<OverlayTrigger
													placement="right"
													overlay={<Tooltip id="tooltip-not">Negate</Tooltip>}>
													<Form.Check
														title="Not"
														name="sai"
														checked={
															item?.lac?.notChecked ||
															item?.sac?.notChecked ||
															item?.plmnId?.mnc?.notChecked ||
															item?.plmnId?.mcc?.notChecked
														}
														type="switch"
														data-switch="true"
														id="custom-switch-sai"
														className="custom-switch-lg custom-switch-position-relative"
														onChange={() =>
															updateNotChecked(formData.id, ['sai'], index)
														}
													/>
												</OverlayTrigger>
											</InputGroup>
											{index === 0 && (
												<Button
													onClick={() => onAddField(formData.id, 'sai')}
													type="button"
													className="border-0 bg-transparent d-inline position-absolute top-0 end-0 pr-2 mt-n3"
													data-bs-toggle="tooltip"
													data-bs-placement="top"
													title="Add SAI"
													aria-expanded="true">
													<i
														className="bi-1 bi-plus-circle-fill text-success"
														title="Add"
														data-toggle="tooltip"
														data-original-title="Add"
													/>
												</Button>
											)}
											{index !== 0 && (
												<OverlayTrigger
													overlay={
														<Tooltip id="tooltip-disabled">
															{`Delete SAI #${index + 1}`}
														</Tooltip>
													}>
													<Button
														onClick={() =>
															handleRemoveField(
																formData.id,
																'sai',
																index,
																'SAI',
															)
														}
														type="button"
														className="border-0 bg-transparent d-inline position-absolute top-0 end-0 pr-2 mt-n3"
														data-bs-toggle="collapse"
														data-bs-target="#newresetSAI1"
														aria-expanded="true">
														<i
															className="bi-1 bi-trash3-fill text-danger"
															title=""
															data-toggle="tooltip"
															data-original-title="Delete"
														/>
													</Button>
												</OverlayTrigger>
											)}
											<div className="row">
												{item?.plmnId?.mnc?.active && (
													<div className="col-12 col-lg-4">
														<label htmlFor={`idCondisaiMnc #${index + 1}`}>
															MNC
														</label>
														<InputGroup className="mb-3">
															<Form.Control
																name="mnc"
																value={item?.plmnId?.mnc?.value}
																onChange={e =>
																	handleChange(
																		['sai', 'plmnId', 'mnc'],
																		e,
																		index,
																	)
																}
																onInput={(e: ChangeEvent<HTMLInputElement>) => {
																	const {name} = e.target;
																	const target =
																		e.target as unknown as HTMLInputElement;
																	const val = target.value.slice(0, 3);
																	target.value = val;
																	const rule = validationRules[
																		name as never
																	] as any;
																	const result =
																		val === '' || rule.regex.test(val);
																	setIsValid(prev => {
																		const saiValid = [...(prev.sai || [])];
																		if (!saiValid[index])
																			saiValid[index] = {
																				mcc: true,
																				mnc: true,
																				sac: true,
																				lac: true,
																			};
																		saiValid[index] = {
																			...saiValid[index],
																			[name]: result,
																		};
																		return {...prev, sai: saiValid};
																	});
																	onObserver(result);
																}}
																className={
																	!isFieldValid(
																		item?.plmnId?.mnc?.active,
																		item?.plmnId?.mnc?.value,
																		validationRules.mnc.regex,
																	)
																		? 'is-invalid'
																		: ''
																}
																aria-label="mnc"
															/>
															{item?.plmnId?.mnc?.active &&
																item?.plmnId?.mnc?.value !== '' &&
																!isValid.sai?.[index]?.mnc && (
																	<div className="invalid-feedback">
																		{validationRules.mnc.errorMessage}
																	</div>
																)}
														</InputGroup>
													</div>
												)}

												{item?.plmnId?.mcc?.active && (
													<div className="col-12 col-lg-4">
														<label htmlFor={`idCondiSAIMcc #${index + 1}`}>
															MCC
														</label>
														<InputGroup className="mb-3">
															<Form.Control
																name="mcc"
																value={item?.plmnId?.mcc?.value}
																onChange={e =>
																	handleChange(
																		['sai', 'plmnId', 'mcc'],
																		e,
																		index,
																	)
																}
																onInput={(e: ChangeEvent<HTMLInputElement>) => {
																	const {name} = e.target;
																	const target =
																		e.target as unknown as HTMLInputElement;
																	const val = target.value.slice(0, 3);
																	target.value = val;
																	const rule = validationRules[
																		name as never
																	] as any;
																	const result =
																		val === '' || rule.regex.test(val);
																	setIsValid(prev => {
																		const saiValid = [...(prev.sai || [])];
																		if (!saiValid[index])
																			saiValid[index] = {
																				mcc: true,
																				mnc: true,
																				sac: true,
																				lac: true,
																			};
																		saiValid[index] = {
																			...saiValid[index],
																			[name]: result,
																		};
																		return {...prev, sai: saiValid};
																	});
																	onObserver(result);
																}}
																className={
																	!isFieldValid(
																		item?.plmnId?.mcc?.active,
																		item?.plmnId?.mcc?.value,
																		validationRules.mcc.regex,
																	)
																		? 'is-invalid'
																		: ''
																}
																aria-label="mcc"
															/>
															{item?.plmnId?.mcc?.active &&
																item?.plmnId?.mcc?.value !== '' &&
																!isValid.sai?.[index]?.mcc && (
																	<div className="invalid-feedback">
																		{validationRules.mcc.errorMessage}
																	</div>
																)}
														</InputGroup>
													</div>
												)}

												{item?.lac?.active && (
													<div className="col-12 col-lg-4">
														<label htmlFor={`idCondiLACEUTRA #${index + 1}`}>
															LAC
														</label>
														<InputGroup className="mb-3">
															<Form.Control
																name="lac"
																value={item?.lac?.value}
																onChange={e =>
																	handleChange(['sai', 'lac'], e, index)
																}
																aria-label="lac"
																onInput={(e: ChangeEvent<HTMLInputElement>) => {
																	const {name} = e.target;
																	const target =
																		e.target as unknown as HTMLInputElement;
																	const val = target.value.slice(0, 4);
																	target.value = val;
																	const rule = validationRules[
																		name as never
																	] as any;
																	const result = rule.regex.test(val);
																	setIsValid(prev => {
																		const saiValid = [...(prev.sai || [])];
																		if (!saiValid[index])
																			saiValid[index] = {
																				mcc: true,
																				mnc: true,
																				lac: true,
																				sac: true,
																			};
																		saiValid[index] = {
																			...saiValid[index],
																			[name]: result,
																		};
																		return {...prev, sai: saiValid};
																	});
																	onObserver(result);
																}}
																className={
																	!isFieldValid(
																		item?.lac?.active,
																		item?.lac?.value,
																		validationRules.lac.regex,
																	)
																		? 'is-invalid'
																		: ''
																}
															/>
															{item?.lac?.active &&
																item?.lac?.value !== '' &&
																!isValid.sai?.[index]?.lac && (
																	<div className="invalid-feedback">
																		{validationRules.lac.errorMessage}
																	</div>
																)}
														</InputGroup>
													</div>
												)}

												{item?.sac?.active && (
													<div className="col-12 col-lg-4">
														<label htmlFor={`idCondiSAC #${index + 1}`}>
															SAC
														</label>
														<InputGroup className="mb-3">
															<Form.Control
																name="sac"
																value={item?.sac?.value}
																onChange={e =>
																	handleChange(['sai', 'sac'], e, index)
																}
																aria-label="sac"
																onInput={(e: ChangeEvent<HTMLInputElement>) => {
																	const {name} = e.target;
																	const target =
																		e.target as unknown as HTMLInputElement;
																	const val = target.value.slice(0, 4);
																	target.value = val;
																	const rule = validationRules[
																		name as never
																	] as any;
																	const result = rule.regex.test(val);
																	setIsValid(prev => {
																		const saiValid = [...(prev.sai || [])];
																		if (!saiValid[index])
																			saiValid[index] = {
																				mcc: true,
																				mnc: true,
																				lac: true,
																				sac: true,
																			};
																		saiValid[index] = {
																			...saiValid[index],
																			[name]: result,
																		};
																		return {...prev, sai: saiValid};
																	});
																	onObserver(result);
																}}
																className={
																	!isFieldValid(
																		item?.sac?.active,
																		item?.sac?.value,
																		validationRules.sac.regex,
																	)
																		? 'is-invalid'
																		: ''
																}
															/>
															{item?.sac?.active &&
																item?.sac?.value !== '' &&
																!isValid.sai?.[index]?.sac && (
																	<div className="invalid-feedback">
																		{validationRules.sac.errorMessage}
																	</div>
																)}
														</InputGroup>
													</div>
												)}
											</div>
										</FieldSetSumma>
									))}
								</div>
							</FieldSetSumma>
						)}
					</div>
				)}
				{/* end 1 row*/}

				{/* 1 row */}
				{(formData?.deviceModel?.[0]?.active ||
					formData?.deviceTac?.[0]?.active) && (
					<div className="row">
						<div className="col-lg-12 bg-gray-400 rounded pt-1 pb-1 mt-2 mb-2 text-center">
							<b>Device related</b>
						</div>
						{formData.deviceModel?.[0]?.active && (
							<FieldSetSumma title={'Device Model'} key={'keyDeviceModel'}>
								<div className="row">
									{formData?.deviceModel?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Device Model #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(
															formData.id,
															['deviceModel'],
															index,
														)
													}
												/>
												<Form.Control
													name="deviceModel"
													value={item?.value}
													required={item?.active}
													onChange={e =>
														handleChange(['deviceModel'], e, index)
													}
													aria-label="deviceModel"
													className={
														isIndexedField('deviceModel') &&
														isValid.deviceModel?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() =>
															onAddField(formData.id, 'deviceModel')
														}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Device Model #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'deviceModel',
																	index,
																	'Device Model',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('deviceModel') &&
													isValid.deviceModel?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.deviceModel.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}

						{formData.deviceTac?.[0]?.active && (
							<FieldSetSumma title={'Device TAC'} key={'keyDeviceTAC'}>
								<div className="row">
									{formData?.deviceTac?.map((item: any, index: number) => (
										<div className="col-xs-12 col-lg-6" key={item.uuid}>
											<label htmlFor="">{`Device TAC #${index + 1}`}</label>
											<InputGroup className="mb-3">
												<InputGroup.Checkbox
													title="Negate"
													checked={item?.notChecked}
													onChange={() =>
														updateNotChecked(formData.id, ['deviceTac'], index)
													}
												/>
												<Form.Control
													name="deviceTac"
													value={item?.value}
													required={item?.active}
													onChange={e => handleChange(['deviceTac'], e, index)}
													aria-label="deviceTac"
													onInput={(e: ChangeEvent<HTMLInputElement>) => {
														const {name} = e.target;
														const target =
															e.target as unknown as HTMLInputElement;
														const val = target.value.slice(0, 8);
														target.value = val;
														const rule = validationRules[name as never] as any;
														const result = rule.regex.test(val);
														if (isIndexedField(name)) {
															setIsValid(prev => ({
																...prev,
																[name]: {
																	...(prev[name] ?? {}),
																	[index]: result,
																},
															}));
														}
														onObserver(result);
													}}
													className={
														isIndexedField('deviceTac') &&
														isValid.deviceTac?.[index] === false
															? 'is-invalid'
															: ''
													}
												/>
												{index === 0 && (
													<Button
														onClick={() => onAddField(formData.id, 'deviceTac')}
														variant="outline-secondary">
														<span className="font-weight-bold text-success">
															+
														</span>
													</Button>
												)}
												{index !== 0 && (
													<OverlayTrigger
														overlay={
															<Tooltip id="tooltip-disabled">
																{`Delete Device TAC #${index + 1}`}
															</Tooltip>
														}>
														<Button
															onClick={() =>
																handleRemoveField(
																	formData.id,
																	'deviceTac',
																	index,
																	'Device TAC',
																)
															}
															variant="outline-secondary">
															<span className="font-weight-bold text-danger">
																-
															</span>
														</Button>
													</OverlayTrigger>
												)}
												{isIndexedField('deviceTac') &&
													isValid.deviceTac?.[index] === false && (
														<div className="invalid-feedback">
															{validationRules.deviceTac.errorMessage}
														</div>
													)}
											</InputGroup>
										</div>
									))}
								</div>
							</FieldSetSumma>
						)}
					</div>
				)}
				{/* end 1 row*/}
			</div>
		</>
	);
};

export default CustomSelectionItemCondition;
